import React from "react"
import { NavLink } from "react-router-dom"
import { useTranslation } from "react-i18next"

import browserStorageService, { REMEMBER_MAIL_KEY } from "services/browserStorage"

import { formStatusEnum, FormElement } from "components/Form"
import { Button } from "components/Elements/Button"

import { useForm, useAuth, useAlert, useOperator } from "hooks"
import { publicRoutes } from "router/routes"
import { ErrorFn, ErrorResponse } from "models"

type SigninFormProps = Record<string, never>

const defaultFormFields = {
	email: "",
	password: "",
	rememberMail: false,
}

export const SigninForm: React.FC<SigninFormProps> = () => {
	const {
		formFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		showGlobalError,
		handleValidation,
	} = useForm({
		defaultFormFields: defaultFormFields,
		defaultFormStatus: formStatusEnum.ready,
	})

	const { t } = useTranslation()
	const { login } = useAuth()
	const { alert } = useAlert()
	const { currentOperator } = useOperator()

	const isLoading = formStatus === formStatusEnum.loading

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()

		// Validate inputs
		const formReady = !(errors.length > 0)
		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				if (formFields.rememberMail) {
					browserStorageService.set(REMEMBER_MAIL_KEY, formFields.email)
				} else {
					browserStorageService.remove(REMEMBER_MAIL_KEY)
				}

				const { getError } = (await login(
					formFields.email,
					formFields.password,
					currentOperator?.id
				)) as ErrorFn
				if (getError) throw getError()
				// useAuth will do the rest and connect the user here
			} catch (error) {
				console.error(error)
				const erroMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", erroMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		className: "mb-2",
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<form id="formSignIn">
			{showGlobalError && (
				<p className="danger mb-6">
					{t("signin.global_error", {
						ns: "auth",
					})}
				</p>
			)}

			<h2 className="font-bold text-4xl mb-10 text-center">
				{t("signin.title", {
					ns: "auth",
				})}
			</h2>

			<div>
				<h3 className="font-bold text-center mb-2 max-w-sm mx-auto block text-primary">
					{t("signin.introduction", {
						ns: "auth",
					})}
				</h3>

				{/* Mail */}
				<FormElement
					{...formElementCommonProps}
					value={formFields.email}
					name="email"
					type="email"
					id="email"
					label={t("email", {
						ns: "user",
					})}
					required={true}
					autocomplete="new-email"
				/>

				{/* Password */}
				<FormElement
					{...formElementCommonProps}
					value={formFields.password}
					name="password"
					type="password"
					id="password"
					label={t("password", {
						ns: "user",
					})}
					required={true}
					autocomplete="new-password"
				/>
			</div>
			<div className="flex justify-end text-sm">
				<NavLink
					to={publicRoutes.lostPassword.path}
					className="underline text-primaryColor"
				>
					{t("forgot_password.title", {
						ns: "auth",
					})}
				</NavLink>
			</div>

			{/* Remember id */}
			<FormElement
				{...formElementCommonProps}
				value={formFields.rememberMail}
				name="rememberMail"
				type="checkbox"
				id="rememberMail"
				label=""
				checkboxLabel={t("signin.remember_mail", {
					ns: "auth",
				})}
				className="mb-6"
			/>

			<div className="flex justify-center">
				<Button
					variant="secondary"
					isLoading={isLoading}
					type="submit"
					onClick={handleSubmit}
					className={"px-4"}
				>
					{t("signin.connexion", {
						ns: "auth",
					})}
				</Button>
			</div>
		</form>
	)
}
